import {
  Activity,
  ArrowRightLeft,
  BadgeCheck,
  Banknote,
  BarChart3,
  BookOpen,
  Building2,
  CalendarDays,
  ClipboardCheck,
  Coins,
  CopyCheck,
  DatabaseBackup,
  FileBarChart,
  FileText,
  Hash,
  Inbox,
  KeyRound,
  Landmark,
  LayoutTemplate,
  LayoutDashboard,
  LineChart,
  ListChecks,
  Mail,
  MailCheck,
  MessagesSquare,
  Percent,
  PiggyBank,
  Radar,
  Receipt,
  Repeat,
  Scale,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Timer,
  TrendingUp,
  Share2,
  Truck,
  Webhook,
  Workflow,
  Undo2,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { toPath, type Route } from './router';

/**
 * Die Gliederung der Oberfläche als Daten — eine Tabelle, vier Leser.
 *
 * **Warum eine eigene Datei und nicht weiter in `Shell.tsx`.** Vier Dinge lesen
 * dieselbe Gliederung: die Menüspalte, die Brotkrumen, die Markierung des
 * aktiven Punktes und der Knopf «Neu» der Kopfzeile. Vier Leser einer Tabelle,
 * die in einer Bauteildatei steht, sind vier Gelegenheiten für eine zweite
 * Tabelle — und zwei Gliederungen, die auseinanderlaufen, sieht niemand, weil
 * jede für sich stimmt.
 *
 * **Zwei Stufen, und die zweite ist neu.** Bisher war die Spalte eine flache
 * Liste aus drei Rubriken. Jetzt stehen aufklappbare Kategorien UND direkte
 * Verweise nebeneinander; welche Form ein Eintrag hat, entscheidet `art`.
 *
 * **Ein Menüeintrag ohne Ziel wird nicht angelegt.** Das Übergabepaket führt
 * Angebote, Mahnwesen, Kassenbuch, Berichte, USt-Voranmeldung und den
 * DATEV-Export; nichts davon gibt es hier. Ein Punkt, der auf eine leere Seite
 * führte, wäre eine Behauptung über Funktionsumfang — und der Anwender suchte
 * den Fehler bei sich.
 */

/**
 * Die Zählquellen, für die es einen Endpunkt gibt — und nur diese.
 *
 * **Wo keine Zahl ist, ist kein Abzeichen**: keine Null, kein Punkt, keine
 * graue Andeutung. Für «Bankkonten» gäbe es keine kontenübergreifende Zahl
 * offener Umsätze (Umsätze werden je Konto geholt), für «Belege» keinen
 * Zahlungszustand am Belegkopf. Eine geschätzte Zahl ist schlimmer als keine:
 * Der Anwender richtet seinen Tag danach. `E-324` prüft genau diese
 * Bedingung — nicht eine Anzahl.
 *
 * **`kontierungWartend` seit `W-freigabeliste` (2026-08-27), die vierte Quelle.**
 * `GET /api/v1/kontierungen/wartend` liefert bereits die volle, gefilterte
 * Liste — derselbe Zählweg wie bei `dubletten` (`.length` über die Antwort
 * eines bestehenden Listen-Endpunkts, kein eigener Zählendpunkt).
 */
export type AbzeichenQuelle =
  | 'eingangskorb'
  | 'dubletten'
  | 'kreditorenerwartung'
  | 'kontierungWartend'
  | 'webhooksErschoepft'
  /**
   * `portalVorgaengeOffen` seit `KP-oberflaeche` (2026-09-17), die sechste
   * Quelle. Derselbe Zählweg wie `dubletten` und `kontierungWartend`:
   * `.length` über die Antwort eines bestehenden Listen-Endpunkts
   * (`GET /api/v1/portal-vorgaenge?status=offen`), kein eigener
   * Zählendpunkt.
   *
   * **Eine Arbeitszahl, die auf null gehen soll** — anders als die Zahl der
   * offenen Posten, die der normale Bestand eines Betriebs ist: Eine
   * Rückfrage eines Kunden wartet auf einen Menschen, und sie zu übersehen
   * ist der Fehler, gegen den das Abzeichen steht.
   */
  | 'portalVorgaengeOffen'
  /**
   * `maileingangFehler` seit `EM-web` (2026-11-04), die siebte Quelle: die
   * Zahl der aktiven E-Mail-Konten, deren letzter Abruf scheiterte. Gezählt
   * über `GET /api/v1/maileingang/konten` (Feld `letzterFehler`), kein eigener
   * Zählendpunkt. Eine Arbeitszahl, die auf null gehen soll: Ein Postfach,
   * das nicht abgerufen wird, liefert keinen Beleg in den Eingangskorb.
   */
  | 'maileingangFehler';

/**
 * Eine Bedingung, die nicht aus den Fähigkeiten folgt, sondern aus dem Zustand
 * der Installation — und deshalb die Schale mitbringt (`Shell.tsx`).
 *
 * **Seit `LX-webseite` (2026-10-16) die erste:** `vorsystemAngelegt` — die
 * Übernahme aus einem Vorsystem ist angelegt (`GET /api/v1/vorsystem/uebernahme/angelegt`:
 * ein Bit und nicht der ganze Stand, `E-2002`). Eine Installation ohne Vorsystem soll den Menüpunkt «Übernahme»
 * nie sehen: Er führte auf eine Seite, die nichts zu tun hat. **Fehlt die
 * Auskunft** (noch nicht geladen, Fehlschlag), gilt die Bedingung als nicht
 * erfüllt — ein Menüpunkt, der erst nach einem Augenblick erscheint, ist kein
 * Fehler; einer, der ohne Grund erscheint, wäre einer.
 */
export type NavBedingung = 'vorsystemAngelegt';

/**
 * Eine Funktion der Systemeinstellungen, die ein Administrator ausschalten
 * kann (`funktion_schalter`, `D-57`) — heute wirksam nur `guthaben`.
 */
export type NavFunktion = 'guthaben';

/** Die gezählten Stände. Ein fehlender Eintrag heisst «nicht bekannt», nicht «null». */
export type Abzeichenstand = Partial<Readonly<Record<AbzeichenQuelle, number>>>;

/**
 * Die Haupthandlung einer Adresse — das Ziel des Kopfzeilenknopfes «Neu».
 *
 * **Der Knopf heisst «Neu» und nicht «Beleg anlegen».** Die Haupthandlung
 * bleibt zusätzlich in der Tafel der Seite, wo sie heute steht; zwei Knöpfe mit
 * demselben zugänglichen Namen auf derselben Seite wären in Playwrights
 * strenger Eindeutigkeit ein Fehlschlag und kein Treffer. Das Ziel steht
 * deshalb im `titel` und landet als `title`-Attribut am Knopf — es beschreibt
 * ihn, ohne seinen Namen zu ändern.
 */
export interface NavHauptaktion {
  readonly ziel: Route;
  readonly titel: string;
  readonly capability: string;
}

/** Ein Navigationspunkt samt der Fähigkeit, die ihn sichtbar macht. */
export interface NavPunkt {
  readonly route: Route;
  readonly label: string;
  /**
   * Fehlt sie, ist der Punkt für jeden angemeldeten Benutzer sichtbar.
   *
   * **Bisher war das Feld an jedem Punkt Pflicht — die Übersicht ist der
   * erste, der ohne Fähigkeit auskommt** (Spec 8, Task 13). Sie zeigt nur
   * Zahlen, die ihre eigenen Abrufe ohnehin je nach Fähigkeit des Anwenders
   * liefern oder mit einem Fehler quittieren (`UebersichtPage.tsx`) — ein
   * zusätzliches Gatter am Menüpunkt behauptete eine Fähigkeit, die es dafür
   * nicht braucht.
   */
  readonly capability?: string;
  /**
   * Oder-Liste: sichtbar, sobald EINE der Fähigkeiten vorliegt. Eingeführt
   * für die Guthaben-Erstattungen (Controller-Entscheid beim Stapeln,
   * 2026-08-31): Anleger (`guthaben:erstatten`) und Freigeber
   * (`guthaben:erstatten:freigeben`) haben beide einen Anwendungsfall auf
   * derselben Seite — die Seite selbst gated ihre Abschnitte je Fähigkeit.
   */
  readonly capabilityAny?: readonly string[];
  /**
   * Das Sinnbild vor der Beschriftung. Es steht NEBEN dem Text und nie statt
   * seiner: Der zugängliche Name des Verweises ist weiterhin genau «Journal»,
   * weil das `<svg>` `aria-hidden` trägt. Damit findet `getByRole('link', {
   * name: 'Journal' })` ihn unverändert — und ein Vorleseprogramm liest keine
   * Bilderrätsel vor.
   */
  readonly icon: LucideIcon;
  /**
   * Zusätzlich zur Fähigkeit: eine Bedingung des Installationszustands (`NavBedingung`).
   * Der Punkt ist nur sichtbar, wenn die Schale sie als erfüllt meldet.
   */
  readonly bedingung?: NavBedingung;
  /**
   * Die Funktion, zu der der Punkt gehört (`D-57`, `E-2078`). Ist sie in den
   * Systemeinstellungen ausgeschaltet, verschwindet der Punkt — anders als eine
   * {@link NavBedingung}, die ein Punkt erst erfüllen muss, ist eine Funktion
   * sichtbar, bis die Schale sie als ausgeschaltet meldet. Heute nur das
   * Kundenguthaben.
   */
  readonly funktion?: NavFunktion;
  /** Nur bei den drei Punkten, für die ein Endpunkt eine Zahl liefert. */
  readonly abzeichen?: AbzeichenQuelle;
  /** Nur bei den Punkten, deren Seite eine Haupthandlung mit eigener Adresse hat. */
  readonly hauptaktion?: NavHauptaktion;
}

/** Eine aufklappbare Kategorie: Beschriftung, Sinnbild, Unterpunkte. */
export interface NavKategorie {
  readonly art: 'kategorie';
  /** React-Schlüssel, Merkschlüssel des Klappzustands und Kennung der aktiven Kategorie. */
  readonly key: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly punkte: readonly NavPunkt[];
}

/** Ein direkter Verweis auf der ersten Stufe — ohne Klappe, ohne Kinder. */
export interface NavVerweis {
  readonly art: 'verweis';
  readonly punkt: NavPunkt;
}

export type NavEintrag = NavKategorie | NavVerweis;

/**
 * Die Gliederung. Eine spätere Umgruppierung ist eine verschobene Zeile.
 *
 * **Die Reihenfolge ist die Arbeitsreihenfolge und nicht das Alphabet:**
 * Verkauf und Einkauf sind das Tagesgeschäft, Bankkonten der Abgleich,
 * Buchhaltung die Auswertung des Erfassten, Stammdaten die Einrichtung.
 *
 * **Bankkonten ist ein direkter Verweis und keine Kategorie.** Eine Kategorie,
 * die man aufklappt, um genau eine Zeile zu sehen, ist ein Klick ohne Ertrag.
 * Die Unterseiten eines Kontos — Einlesen, Auszüge, Abruf, Klärliste — sind
 * Unterseiten und keine Menüpunkte; sie markieren über `SECTION` diesen einen
 * Verweis.
 *
 * **Kommt ein weiterer Eintrag dazu** — «Übersicht» als direkter Verweis, eine
 * FinTS-Ansicht unter den Bankkonten —, ist das eine Zeile in dieser Tabelle
 * und keine Änderung an `Sidebar.tsx`: Beide Formen, Kategorie und Verweis,
 * werden bereits gerendert, und der Klappzustand liest seine Schlüssel aus
 * dieser Tabelle statt aus einer zweiten Liste.
 */
export const NAV: readonly NavEintrag[] = [
  // **Steht als Erstes, ohne Fähigkeit.** Die Übersicht zeigt nur Zahlen, die
  // ihre eigenen Abrufe ohnehin je nach Fähigkeit des Anwenders liefern oder
  // mit einem Fehler quittieren — der Menüpunkt selbst braucht deshalb keine
  // eigene Voraussetzung (Spec 8, Task 13 `[V-ovroute]`).
  {
    art: 'verweis',
    punkt: {
      route: { name: 'overview' },
      label: 'Übersicht',
      icon: LayoutDashboard,
      // Keine Haupthandlung: Die Seite zeigt nur Zahlen, keine eigene
      // Erstellung.
    },
  },
  // **Belegsuche, unmittelbar nach der Übersicht und OHNE `capability`**
  // (Spec 16.3). Der Endpunkt verlangt *mindestens eine* der sechs
  // Lesefähigkeiten — ein Begriff, den `NavPunkt.capability` nicht ausdrücken
  // kann; es trägt genau EINE. Ein Menüpunkt, der willkürlich eine der sechs
  // nennte, verschwände für jemanden, der die anderen fünf hat. Die Seite
  // fragt selbst und beantwortet die Frage selbst — dieselbe Begründung wie
  // bei der Übersicht eine Zeile darüber.
  //
  // **Kein Eintrag unter «Buchhaltung», und das ist eine Abweichung vom
  // Bildschirmentwurf** (er zeigt die Brotkrume `BUCHHALTUNG · …`). Die Suche
  // läuft über ALLE sechs Bestände, auch über Kunden und Kreditoren; sie
  // unter «Buchhaltung» einzusortieren behauptete eine Eingrenzung, die es nicht
  // gibt. Der Auftraggeber kann überstimmen — es kostet eine verschobene
  // Zeile in dieser Tabelle und sonst nichts.
  {
    art: 'verweis',
    punkt: {
      // Leere Anfrage: Der Menüpunkt öffnet die Seite, er stellt keine Frage.
      route: { name: 'suche', q: '' },
      label: 'Belegsuche',
      icon: Search,
      // Keine Haupthandlung: Eine Suche legt nichts an.
    },
  },
  {
    art: 'kategorie',
    key: 'verkauf',
    label: 'Verkauf',
    icon: FileText,
    punkte: [
      {
        route: { name: 'documents' },
        label: 'Belege',
        capability: 'document:read',
        icon: FileText,
        // Kein Abzeichen: Der Belegkopf trägt keinen Zahlungszustand, und
        // «überfällig» wäre damit eine Zahl, die es hier nicht gibt.
        hauptaktion: {
          ziel: { name: 'document-new' },
          titel: 'Beleg anlegen',
          capability: 'document:manage',
        },
      },
      // **Die wiederkehrenden Rechnungen stehen unter «Verkauf», direkt hinter
      // den Belegen** (`F-aboliste`). Eine Vorlage ist keine eigene Rubrik: Sie
      // erzeugt Ausgangsrechnungen und nichts sonst, und was sie erzeugt, steht
      // eine Zeile darüber. Ein eigener Punkt der ersten Stufe behauptete einen
      // Bereich neben dem Verkauf, den es fachlich nicht gibt.
      //
      // **Sichtbar mit `document:read`** — dieselbe Fähigkeit, die die beiden
      // Leseendpunkte tragen, und die jede Rolle hat. Das Schreiben hängt an
      // `aborechnung:manage`, das Scharfschalten zusätzlich an
      // `aborechnung:scharfschalten`; darüber entscheidet die Seite selbst und
      // bei jeder Anfrage der Wächter neu.
      //
      // Kein Abzeichen: Die denkbare Zahl wäre «heute fällig», und die gibt
      // kein Endpunkt her — eine geschätzte wäre schlechter als keine.
      {
        route: { name: 'aborechnungen' },
        label: 'Aborechnungen',
        capability: 'document:read',
        icon: Repeat,
        hauptaktion: {
          ziel: { name: 'aborechnung-new' },
          titel: 'Aborechnung anlegen',
          capability: 'aborechnung:manage',
        },
      },
      // «Lastschrift» direkt hinter «Aborechnungen» (`LS-einzug`, Task 3,
      // Spec Abschnitt 5, `D-16`) — die Dateiseite der SEPA-Lastschrift-Ära.
      // Der Reiter, der die FÄLLIGEN Belege zeigt, sitzt unter «Belege»
      // selbst (`DocumentsPage.tsx`); dieser Punkt führt zum ARCHIV der
      // erzeugten Dateien. Sichtbar mit `lastschrift:einzug` und nicht mit
      // `document:read`: Wer die Datei-Ära nicht bedienen darf, hat für ihr
      // Archiv keinen eigenen Anwendungsfall — dieselbe Schwelle wie beim
      // Vormerken selbst (`D-11` der Lastschrift-Spec). Kein Abzeichen: Die
      // denkbare Zahl wäre «Dateien in `erstellt`», und die Arbeitsliste
      // dafür ist `lastschrift:offen`, ein Handlauf und kein Endpunkt.
      {
        route: { name: 'lastschrift' },
        label: 'Lastschrift',
        capability: 'lastschrift:einzug',
        icon: Banknote,
      },
      // Nummernkreise und Aussteller waren nie Belege, sondern deren
      // Einrichtung. In einer flachen Liste fiel das nicht auf; in einer
      // Kategorie namens «Verkauf» stehen sie richtig.
      {
        route: { name: 'number-ranges' },
        label: 'Nummernkreise',
        capability: 'document:read',
        icon: Hash,
      },
      // Die ANZEIGE hängt am Leserecht, das Ändern an `issuer:manage` — und
      // darüber entscheidet der Wächter der API bei jeder Anfrage neu.
      {
        route: { name: 'issuer' },
        label: 'Rechnungssteller',
        capability: 'document:read',
        icon: Building2,
      },
      // **Die E-Mail-Vorlagen stehen unter «Verkauf», hinter den
      // Rechnungsstellern** (`F-mailmaske`). Sie sind wie Nummernkreise und
      // Rechnungssteller keine Belege, sondern deren Einrichtung — was hier
      // gepflegt wird, ist der Text, mit dem ein Beleg das Haus verlässt. Eine
      // eigene Rubrik behauptete einen Bereich neben dem Verkauf, den es
      // fachlich nicht gibt; unter den Einstellungen (so der Wortlaut der
      // Spec) gibt es in dieser Oberfläche keinen Ort — dieselbe Einordnung,
      // die schon Nummernkreise und Aussteller aus einer flachen Liste hierher
      // gebracht hat.
      //
      // **Sichtbar mit `document:manage` und nicht mit `document:read`**,
      // anders als die drei Punkte darüber: Das ist die Fähigkeit, die die
      // Leseendpunkte dieser Seite wirklich verlangen (`document:read` genügt
      // dort NICHT). Ein Punkt, der einer Rolle mit blossem Leserecht
      // angeboten wird und ihr dann eine 403 zeigt, ist schlechter als kein
      // Punkt. Das Anfügen hängt an `mailvorlage:manage`, das Scharfschalten
      // zusätzlich an `mailvorlage:scharfschalten`; darüber entscheidet die
      // Seite selbst und bei jeder Anfrage der Wächter neu.
      //
      // Keine Haupthandlung: Eine Vorlage wird über diese Oberfläche nicht
      // angelegt (der Erstbestand bringt sie mit), sondern fortgeschrieben —
      // und das ist ein Dialog der Liste, keine eigene Anlegeadresse, die der
      // Kopfzeilenknopf anspringen könnte. Kein Abzeichen: Die denkbare Zahl
      // wäre «offene Entwürfe», und die gibt kein Endpunkt her.
      {
        route: { name: 'mailvorlagen' },
        label: 'E-Mail-Vorlagen',
        capability: 'document:manage',
        icon: Mail,
      },
    ],
  },
  {
    art: 'kategorie',
    key: 'einkauf',
    label: 'Einkauf',
    icon: Receipt,
    punkte: [
      // Der Punkt heisst «Eingangskorb» und nicht «Eingangsrechnungen»: Was
      // darin liegt, ist erst nach dem Erfassen eine Rechnung —
      // `not_an_invoice` ist einer der drei Verwerfensgründe.
      {
        route: { name: 'eingang' },
        label: 'Eingangskorb',
        capability: 'incoming:read',
        icon: Inbox,
        abzeichen: 'eingangskorb',
        // **Nachgeholt in Chip 5 (`V-belegbank`)** — Chip 2 liess den Punkt
        // ohne Haupthandlung, weil die Ablagefläche, auf die der Knopf zeigen
        // soll, erst hier entsteht (Plan, Abschnitt „Planänderungen"
        // 2026-08-24, Punkt 1). Ziel ist dieselbe Adresse mit `neu: true` und
        // keine eigene Route: „Belege hochladen" hat keine Anlegemaske, nur
        // die Ablagefläche dieser Seite (Spec 7.3). Die Fähigkeit ist
        // `incoming:capture` und NICHT das in Spec 7.3 wörtlich genannte
        // `incoming:write` — dieses Recht gibt es in `auth/capabilities.ts`
        // nicht; `incoming:capture` ist dieselbe Fähigkeit, an der schon der
        // Hochladen-Knopf dieser Seite hängt (`darfErfassen`).
        hauptaktion: {
          ziel: { name: 'eingang', neu: true },
          titel: 'Belege hochladen',
          capability: 'incoming:capture',
        },
      },
      // **Die unauffälligste und die wertvollste Ansicht ihrer Ära.** Sie sagt,
      // von wem NICHTS gekommen ist, und wer nur den Korb öffnet, sieht nur,
      // was da ist. Ein Punkt, den man aufsuchen kann, ist der Unterschied
      // zwischen «fällt auf» und «fällt niemandem auf».
      {
        route: { name: 'kreditorenerwartung' },
        label: 'Erwartete Rechnungen',
        capability: 'incoming:read',
        icon: Timer,
        abzeichen: 'kreditorenerwartung',
      },
    ],
  },
  {
    art: 'verweis',
    punkt: {
      route: { name: 'bank-accounts' },
      label: 'Bankkonten',
      capability: 'bank:read',
      // **Nicht «Konten».** Der Punkt heisst `Bankkonten`, weil `Sachkonten`
      // (bis `W-navbenennung`, 2026-09-01: «Konten») in dieser Oberfläche der
      // Kontenrahmen ist — ein Sachkonto und ein Geldkonto sind zwei
      // verschiedene Dinge, und die Kontenliste nennt in ihrer dritten Spalte
      // genau die Verbindung zwischen beiden. Aus demselben Grund trägt der
      // Punkt `Banknote` und nicht das `Landmark` des Kontenrahmens: Zwei
      // Punkte mit demselben Sinnbild machen die Unterscheidung wieder
      // unsichtbar.
      icon: Banknote,
      // Kein Abzeichen: Es gibt keine kontenübergreifende Zahl offener Umsätze.
      // Umsätze werden je Konto geholt; eine Gesamtzahl bräuchte eine Anfrage
      // je Konto, und geschätzt wäre sie eine Erfindung.
      //
      // **Haupthandlung nachgeholt in Chip 5 (`V-belegbank`)** — Chip 2 liess
      // sie offen, weil sie eine Änderung an `BankAccountsPage.tsx` gebraucht
      // hätte, die ausserhalb seiner Naht lag (Plan, „Planänderungen"
      // 2026-08-24, Punkt 1). Ein Konto entsteht über den Dialog DER LISTE,
      // nicht über eine eigene Adresse; das Ziel ist deshalb dieselbe Route mit
      // `neu: true` statt einer eigenen `bank-account-new`.
      hauptaktion: {
        ziel: { name: 'bank-accounts', neu: true },
        titel: 'Konto anlegen',
        capability: 'bank:manage',
      },
    },
  },
  {
    art: 'kategorie',
    key: 'buchhaltung',
    label: 'Buchhaltung',
    icon: BookOpen,
    punkte: [
      { route: { name: 'journal' }, label: 'Journal', capability: 'journal:read', icon: BookOpen },
      // **«Sachkonten» statt «Konten» seit `W-navbenennung` (2026-09-01),**
      // Mitarbeiterinnen-Rückmeldung vom 2026-08-31 wörtlich: „Konten soll zu
      // 'Sachkonten' umbenannt werden im Menü." Die Route bleibt `accounts`
      // (`/konten`) — ein Pfadwechsel bräche Lesezeichen und e2e ohne Ertrag,
      // die Rückmeldung nannte nur die Beschriftung. Die Seite selbst
      // (`AccountsPage.tsx`, Überschrift, `loadingLabel`) gehört dem Chip
      // `W-sachkonten`.
      { route: { name: 'accounts' }, label: 'Sachkonten', capability: 'account:read', icon: Landmark },
      {
        route: { name: 'periods' },
        label: 'Perioden',
        capability: 'period:read',
        icon: CalendarDays,
      },
      // **Seit `V-klaerliste4` (Aufgabe 10).** Vorher hing die Klärliste
      // allein an einem Zahldienstkonto (`zahldienst-klaerliste`, `SECTION`
      // unten) und hatte deshalb keinen eigenen Menüpunkt — man kam nur über
      // die Kontoansicht dorthin. Jetzt ist sie eine kontenübergreifende
      // Berichtsseite (Spec 4.5, `A-K4`) und braucht eine eigene Adresse im
      // Hauptmenü; `bank:read`, dieselbe Fähigkeit wie ihr Endpunkt.
      {
        route: { name: 'klaerliste' },
        label: 'Kontenabgleich',
        capability: 'bank:read',
        icon: ListChecks,
        // Seit `W-freigabeliste` (2026-08-27): die Zahl wartender Freigaben,
        // aus demselben Grund, aus dem der Eingangskorb ein Abzeichen trägt —
        // eine Zahl, die den Tag bestimmt, gehört ins Menü und nicht nur auf
        // die Seite, die man erst öffnen muss, um sie zu sehen.
        abzeichen: 'kontierungWartend',
      },
      // **Seit `ZZ-web` (Zahlungszuordnung Stufe 1, Task 7).** Die
      // Klärungsliste der Geldeingänge ohne Zuordnung — **neben**
      // «Kontenabgleich» und nicht darin (`D-26`): Jener beantwortet «welches
      // Sachkonto», diese «welche Rechnung». `bank:read`, dieselbe Fähigkeit
      // wie ihr Endpunkt; wer den Klärfall dann wirklich zuordnet, braucht
      // `zahlung:zuordnen` — an dem Knopf, der schreibt, und nicht an dem
      // Menüpunkt, der zeigt.
      //
      // **Kein Abzeichen.** Die Zahl der Klärfälle geht nach dem Cutover
      // nicht auf null und soll es auch nicht: 95,6 % der gemessenen
      // Geldeingänge tragen keine Rechnungsnummer, und ein Abzeichen mit
      // vierstelliger Zahl neben dem Menüpunkt behauptete einen Rückstand,
      // wo der Normalbestand steht. Dieselbe Erwägung wie bei «Offene
      // Posten» darunter.
      {
        route: { name: 'zahlungsklaerung' },
        label: 'Zahlungsklärung',
        capability: 'bank:read',
        icon: Banknote,
      },
      // **Seit `ZZ-freigabeliste` (2026-09-06).** Die Freigabeliste der
      // Zahlungszuordnungen — der Ort, an dem die Vier-Augen-Regel aus
      // `D-14` tatsächlich stattfindet. Bis dahin hatte
      // `POST /zahlungen/zuordnungen/:id/freigeben` KEINE Oberfläche, und die
      // Klärungsliste konnte ihn nicht mit anbieten: `zahlung_klaerung`
      // (`0161`) führt eine bereits zugeordnete Bewegung ausdrücklich nicht
      // mehr.
      //
      // **`capabilityAny` und nicht `capability`**, aus demselben Grund wie
      // bei den Guthaben-Erstattungen darunter: Die Seite trägt ZWEI
      // Abschnitte für zwei Rollen — die Freigabeliste
      // (`zahlung:freigeben`, Rolle `reviewer`) und den Guthaben-Handweg
      // (`zahlung:zuordnen` UND `guthaben:einloesen`, Rolle `accountant`).
      // Mit einer einzigen Fähigkeit am Menüpunkt sähe genau eine der beiden
      // Rollen die Seite, für die sie gebaut ist. Die Seite selbst blendet
      // ihre Abschnitte je Fähigkeit aus.
      //
      // **Kein Abzeichen.** Sachlich wäre eine Wartezahl hier richtig — sie
      // soll auf null gehen —, aber die Zählquelle existiert nicht
      // (`AbzeichenQuelle` müsste erweitert werden), und ein Abzeichen aus
      // einem zweiten, ungezählten Abruf wäre eine Zahl ohne Herkunft.
      // Vorschlag im Abschlussbericht statt eines erfundenen Zählers.
      {
        route: { name: 'zahlungsfreigabe' },
        label: 'Zahlungsfreigabe',
        capabilityAny: ['zahlung:freigeben', 'zahlung:zuordnen'],
        icon: BadgeCheck,
      },
      // **Seit `C-offeneposten` (Plan Task 14).** Die Offene-Posten-Liste je
      // Kunde — der Saldo des Forderungskontos, abgeleitet aus dem Journal.
      // `document:read`, dieselbe Fähigkeit wie ihr Endpunkt: Sie ist der
      // Sache nach eine Auskunft über Ausgangsrechnungen und ihre Kunden, und
      // eine einzelne Buchungszeile tritt nach aussen nicht.
      //
      // **Kein Abzeichen.** Die Zahl der offenen Posten ist keine Arbeitszahl,
      // die auf null gehen soll — sie ist der normale Bestand eines Betriebs.
      // Ein Abzeichen daneben behauptete einen Rückstand, wo keiner ist.
      {
        route: { name: 'offene-posten' },
        label: 'Offene Posten',
        capability: 'document:read',
        icon: Coins,
      },
      // **Seit `W-abstimmansicht`, nur LESEND.** Die Abgleichsätze der
      // Guthabenabstimmung — der Lauf selbst bleibt ausschliesslich
      // `pnpm guthaben:abstimmung` (CLI), diese Seite zeigt seine Protokolle.
      // `guthaben:abstimmung:lesen` liegt bei reviewer, accountant, admin,
      // wie `bank:read` zwei Zeilen darüber — nicht bei readonly (verteilt wie
      // `audit:read`, siehe `capabilities.ts`).
      {
        route: { name: 'guthaben-abstimmung' },
        label: 'Guthabenabstimmung',
        capability: 'guthaben:abstimmung:lesen',
        icon: Wallet,
        funktion: 'guthaben',
        // Kein Abzeichen: Es gibt keine "wartende" Zahl -- ein Abgleichsatz
        // ist ein abgeschlossener Lauf, kein offener Vorgang.
      },
      // **Seit `V-guthabenauszahlung` (2026-08-28).** Die Freigabeliste der
      // Guthaben-Erstattungen -- `guthaben:erstatten:freigeben`, dieselbe
      // Fähigkeit wie ihr Endpunkt: Nur wer freigeben darf, hat für diese
      // Liste einen eigenen Anwendungsfall (Kopfkommentar
      // `erstattung.controller.ts`). Ein eigener Menüpunkt für die ANLAGE
      // existiert weiterhin nicht -- sie bleibt über den Dialog am Kunden
      // erreichbar (`CustomerPage.tsx`) und ist seit dem Owner-Review vom
      // 2026-08-31 zusätzlich auf DIESER Seite eingebettet
      // (`ErstattungenPage.tsx`, Kundenauswahl vor der Freigabeliste), damit
      // wer über diesen Menüpunkt kommt (also freigeben darf), auch das
      // Anlegen findet, ohne den Kunden zuerst zu suchen. Seit dem
      // Controller-Entscheid beim Stapeln (2026-08-31) sieht auch, wer NUR
      // `guthaben:erstatten` trägt (die Rolle `accountant`), diesen
      // Menüpunkt (capabilityAny) -- die Seite zeigt ihr dann das
      // Anlege-Panel und blendet die Freigabeknöpfe fähigkeitsgemäß aus.
      //
      // Kein Abzeichen (noch): Eine wartende-Zahl wäre hier so sachlich
      // richtig wie bei `kontierungWartend`, aber die Zählquelle existiert
      // noch nicht (`AbzeichenQuelle` müsste erweitert werden) -- Vorschlag
      // im Abschlussbericht dieses Auftrags statt einer Erweiterung im
      // Vorbeigehen.
      {
        route: { name: 'guthaben-erstattungen' },
        label: 'Guthaben-Erstattungen',
        capabilityAny: ['guthaben:erstatten', 'guthaben:erstatten:freigeben'],
        icon: Undo2,
        funktion: 'guthaben',
      },
      // **Seit `GU-korrekturmaske` (2026-09-08).** Die Liste der
      // Guthaben-Korrekturen und ihre Freigabe. `guthaben:abstimmung:lesen`
      // und nicht die Freigabefaehigkeit: Das ist die Faehigkeit, die
      // `GET /guthaben/korrekturen` wirklich verlangt (Kopfkommentar
      // `korrektur.controller.ts` — eine Korrektur ist ein Stueck der
      // Guthabenabstimmung, und haenge das Lesen am Freigaberecht, koennte der
      // Anleger die Liste nicht sehen, die er angelegt hat). Damit ist es
      // dieselbe Faehigkeit wie bei «Guthabenabstimmung» zwei Zeilen darueber
      // — beide Seiten lesen dieselbe Pruefungsspur, und die drei Rollen, die
      // mit Korrekturen zu tun haben (reviewer, accountant, admin), tragen sie
      // bereits.
      //
      // **Ein einzelnes `capability` und kein `capabilityAny`**, anders als
      // bei den Erstattungen darueber: Dort tragen zwei Abschnitte zwei
      // Faehigkeiten, von denen KEINE die andere einschliesst. Hier
      // schliesst die Lesefaehigkeit die Seite fuer jeden auf, der ueberhaupt
      // etwas darauf tun darf — der Freigabeknopf und der Spiegelvorrat sind
      // Abschnitte INNERHALB der Seite, und beide Rollen, die sie brauchen,
      // tragen `guthaben:abstimmung:lesen` ohnehin.
      //
      // **Der Anlass war ein Halt im Cutover.** `scripts/cutover-neuaufbau.mjs`
      // legt in Phase 7 die Anfangsbestandskorrektur an und wartet auf die
      // Freigabe durch einen zweiten Menschen; die API konnte das seit
      // `C-gukorrcrud`, die Oberflaeche hatte dafuer keine Seite.
      //
      // Kein Abzeichen: Eine Wartezahl waere hier so sachlich richtig wie bei
      // den Erstattungen, aber die Zaehlquelle existiert nicht
      // (`AbzeichenQuelle` muesste erweitert werden) — dieselbe Erwaegung wie
      // dort, und derselbe Vorschlag im Abschlussbericht statt eines
      // erfundenen Zaehlers.
      {
        route: { name: 'guthaben-korrekturen' },
        label: 'Guthaben-Korrekturen',
        capability: 'guthaben:abstimmung:lesen',
        icon: SlidersHorizontal,
        funktion: 'guthaben',
      },
      // **Seit `DX-maske` (Task 13).** Der DATEV-Export -- die Schwelle, an
      // der Buchungsdaten das Haus verlassen. `capabilityAny` wie bei den
      // Guthaben-Erstattungen zwei Zeilen darueber und aus demselben Grund:
      // Erzeugen und Freigeben sind zwei Faehigkeiten und zwei Personen
      // (`D-21`), und BEIDE brauchen diesen Menuepunkt -- die Seite blendet
      // dann den Abschnitt aus, den die jeweilige Rolle nicht bedienen darf.
      // Wer keine der beiden traegt, sieht den Punkt nicht: Das Register
      // allein (`journal:read`) ist eine Auskunft und kein Arbeitsplatz.
      //
      // Kein Abzeichen: Die Zahl der offenen Perioden ist kein Rueckstand,
      // sondern der planmaessige Abstand zwischen Periodenschluss und Export
      // -- dieselbe Erwaegung wie bei den offenen Posten weiter oben.
      {
        route: { name: 'datev-export' },
        label: 'DATEV-Export',
        capabilityAny: ['datev:export', 'datev:freigabe'],
        icon: Share2,
      },
      // **Seit `K-geldkontenmaske` (2026-09-08).** Die Freigabeliste der
      // Geldkonto-Anfangsbestände (Migration `0198`, `K-geldkontenanfang`) —
      // der Eröffnungssaldo je Bankkonto, aus dem letzten Kontoauszug
      // abgeleitet und von einer zweiten Person freigegeben.
      //
      // **`kontierung:approve` und keine `capabilityAny`.** Anders als bei
      // den Guthaben-Erstattungen gibt es hier keine zweite Rolle mit einer
      // eigenen Anlage-Fähigkeit: Die Anlage läuft ausschliesslich über den
      // Handlauf `pnpm kontierung:geldkontenanfang -- --run`
      // (Systembenutzer), und `kontierung:write` — die Fähigkeit, mit der
      // sonst angelegt wird — hat für DIESE Liste keinen Anwendungsfall. Wer
      // sie nicht abarbeiten darf, hat für sie keinen eigenen
      // Anwendungsfall (dieselbe Regel wie bei «Guthaben-Erstattungen» und
      // «Kontenabgleich» — der Menüpunkt trägt dieselbe Fähigkeit wie sein
      // Endpunkt, `GeldkontoAnfangsbestandController`).
      //
      // Kein Abzeichen: Es gibt so viele Zeilen wie Geldkonten (drei,
      // Stand `K-geldkontenanfang`), und eine wartende Freigabe ist ein
      // einmaliger Vorgang beim Cutover, kein laufender Rückstand wie
      // `kontierungWartend` — `AbzeichenQuelle` müsste dafür erweitert
      // werden, und eine Zahl, die auf Dauer bei null oder drei steht, ist
      // keine Nachricht.
      {
        route: { name: 'geldkonto-anfangsbestand' },
        label: 'Geldkonten-Anfangsbestand',
        capability: 'kontierung:approve',
        icon: PiggyBank,
      },
      // **Seit `K-saldenvortrag` (2026-10-06, Migration `0285`, `D-53`).**
      // Der Saldenvortrag eines Bilanz-Sachkontos ohne eigene Ableitung —
      // gleich neben dem Geldkonto-Anfangsbestand, weil es derselbe Vorgang ist
      // (9000 zum Journalbeginn, zweite Unterschrift).
      //
      // **`capabilityAny` und nicht `kontierung:approve` allein**, anders als
      // der Nachbar darüber: Dort legt ein Handlauf an, hier die Rolle
      // `accountant` (`kontierung:write`, ohne `kontierung:approve`) — sie muss
      // die Seite erreichen, um anzulegen. Wer keine der beiden trägt, hat für
      // die Seite keinen Anwendungsfall, obwohl die Liste selbst nur
      // `journal:read` verlangt.
      {
        route: { name: 'saldenvortraege' },
        label: 'Saldenvorträge',
        capabilityAny: ['kontierung:write', 'kontierung:approve'],
        icon: Scale,
      },
    ],
  },
  // **Die Auswertungen — Ära vom 2026-09-10, Spec Abschnitt 10.2, Plan Task
  // 2 `[AW-geruest]`.**
  //
  // **Nach «Buchhaltung» und vor «Stammdaten»**, weil die Reihenfolge dieser
  // Leiste die Arbeitsreihenfolge ist (Kopfkommentar von `NAV`): Verkauf und
  // Einkauf sind das Tagesgeschäft, Bankkonten der Abgleich, Buchhaltung die
  // Auswertung des Erfassten — und die Auswertungen sind die Auswertung der
  // Buchhaltung. Stammdaten sind die Einrichtung und stehen weiter darunter.
  //
  // **Kategorie und nicht Verweis**, anders als «Bankkonten»: Acht Zeilen
  // sind genau der Fall, für den es die Kategorie gibt (seit `BWA-grund`,
  // 2026-09-14: die BWA als achte). Eine Kategorie, die man aufklappt, um
  // eine Zeile zu sehen, wäre ein Klick ohne Ertrag; eine, die acht zeigt,
  // ist die Gliederung selbst.
  //
  // **Alle acht tragen dieselbe Fähigkeit `auswertung:read`** (`D-24`) —
  // die Seiten bündeln vier Bestände, und eine Aufteilung je Unterseite
  // behauptete eine Abstufung, die die Rechtematrix nicht macht. Alle vier
  // Rollen haben sie.
  //
  // **Kein Abzeichen an einer einzigen Zeile.** Die denkbare Zahl wäre
  // überall eine Kennzahl — Umsatz, offene Forderungen, Zahllast —, und eine
  // Kennzahl in einem Menüabzeichen wäre eine Auswertung ohne ihre Grenze:
  // genau das, wogegen diese Ära gebaut ist (`D-9`). `AbzeichenQuelle` kennt
  // sie folgerichtig nicht.
  //
  // **Keine Haupthandlung.** Die Seiten zeigen Zahlen und erzeugen nichts;
  // der CSV-Export der angezeigten Tabelle (`D-23`) läuft im Browser aus der
  // Tabelle selbst und hat keine Adresse, die der Kopfzeilenknopf
  // anspringen könnte.
  {
    art: 'kategorie',
    key: 'auswertungen',
    label: 'Auswertungen',
    icon: BarChart3,
    punkte: [
      {
        route: { name: 'auswertung-umsatz' },
        label: 'Umsatz',
        capability: 'auswertung:read',
        icon: TrendingUp,
      },
      {
        route: { name: 'auswertung-kunden' },
        label: 'Kunden',
        capability: 'auswertung:read',
        icon: Users,
      },
      {
        route: { name: 'auswertung-zahlungswege' },
        label: 'Zahlungswege',
        capability: 'auswertung:read',
        icon: Wallet,
      },
      {
        route: { name: 'auswertung-forderungen' },
        label: 'Forderungen',
        capability: 'auswertung:read',
        icon: Timer,
      },
      {
        route: { name: 'auswertung-kosten' },
        label: 'Kosten',
        capability: 'auswertung:read',
        icon: Receipt,
      },
      {
        route: { name: 'auswertung-steuer' },
        label: 'Steuer',
        capability: 'auswertung:read',
        icon: Scale,
      },
      {
        route: { name: 'auswertung-prognose' },
        label: 'Prognose',
        capability: 'auswertung:read',
        icon: LineChart,
      },
      // Achter Punkt seit `BWA-grund` (2026-09-14): die
      // Betriebswirtschaftliche Auswertung nach DATEV-Standard 01 aus dem
      // eigenen Journal (Spec
      // `docs/superpowers/specs/2026-09-14-bwa-design.md`).
      {
        route: { name: 'auswertung-bwa' },
        label: 'BWA',
        capability: 'auswertung:read',
        icon: FileBarChart,
      },
      // Neunter Punkt seit `UV-web` (2026-10-10): das Kennzahlblatt der
      // Umsatzsteuer-Voranmeldung (Spec
      // `docs/superpowers/specs/2026-10-09-ustva-kennzahlblatt-design.md`
      // Abschnitt 5.1), dieselbe Fähigkeit `auswertung:read` wie die Steuerseite (`D-27`).
      {
        route: { name: 'auswertung-ustva' },
        label: 'Umsatzsteuer-Voranmeldung',
        capability: 'auswertung:read',
        icon: Percent,
      },
    ],
  },
  {
    art: 'kategorie',
    key: 'stammdaten',
    label: 'Stammdaten',
    icon: Users,
    punkte: [
      {
        route: { name: 'customers' },
        label: 'Kunden',
        capability: 'customer:read',
        icon: Users,
        hauptaktion: {
          ziel: { name: 'customer-new' },
          titel: 'Kunde anlegen',
          capability: 'customer:manage',
        },
      },
      {
        // Im Übergabepaket heisst dieser Punkt «Lieferanten». Wir bleiben beim
        // Wort des Steuerberaters: Ein Kreditor ist die Gegenseite eines
        // Debitors, und die Kontenlogik trägt dieses Wort.
        route: { name: 'vendors' },
        label: 'Kreditoren',
        capability: 'vendor:read',
        icon: Truck,
        hauptaktion: {
          ziel: { name: 'vendor-new' },
          titel: 'Kreditor anlegen',
          capability: 'vendor:manage',
        },
      },
      {
        route: { name: 'duplicates' },
        label: 'Dubletten',
        capability: 'customer:read',
        icon: CopyCheck,
        abzeichen: 'dubletten',
      },
    ],
  },
  // **Die Arbeitsliste der Portal-Vorgänge — ein direkter Verweis, bewusst
  // NICHT in der Gruppe «System»** (`KP-oberflaeche`, 2026-09-17; ausgegliedert
  // `PF-systemreiter`, 2026-10-06). Sie sind die Gegenrichtung zu «Webhooks»:
  // Dort schickt dieses Haus Nachrichten hinaus, hier kommt etwas von aussen
  // herein — eine Rückfrage zu einem Beleg oder ein Antrag auf Änderung der
  // Rechnungsdaten, beides von einem Endkunden im Kundenportal (`D-5`).
  //
  // **Warum nicht unter «System».** Die Gruppe «System» ist der Ort, an dem der
  // Kunde sein Buchhaltungssystem EINSTELLT; ein Portal-Vorgang stellt nichts
  // ein, er wartet auf einen Menschen — Tagesgeschäft mit Zahl daneben, wie der
  // Eingangskorb. Ein Verweis und keine Kategorie, weil es genau eine Zeile
  // wäre, und er steht unmittelbar VOR «System»: nach den Stammdaten, vor der
  // Einrichtung.
  //
  // **`document:read` und nicht `portal:zugriff`.** Die Portal-Fähigkeit trägt
  // keine Rolle (`D-7`) — ein Menüpunkt dahinter wäre für jeden Menschen
  // unsichtbar. Die zwei Betriebsrouten hängen an `document:read` (lesen) und
  // `document:manage` (erledigen); die Seite blendet den Erledigen-Knopf aus,
  // wo die zweite fehlt, und die Fähigkeit am Menüpunkt ist deshalb die
  // lesende.
  //
  // **Abzeichen mit der Zahl offener Vorgänge**, wie bei «Eingangskorb» und
  // aus demselben Grund: Ein Kunde hat gefragt und wartet. Sichtbar für
  // `document:read`, dieselbe Fähigkeit wie der Punkt selbst — sie ist damit
  // immer vorhanden, wo der Menüpunkt existiert.
  //
  // Keine Haupthandlung: Es gibt nichts anzulegen. Ein Vorgang entsteht
  // ausschliesslich im Portal.
  {
    art: 'verweis',
    punkt: {
      route: { name: 'portal-vorgaenge' },
      label: 'Portal-Vorgänge',
      capability: 'document:read',
      // `MessagesSquare` und nicht `Mail`: Jenes hängt an den
      // E-Mail-Vorlagen unter «Verkauf», und zwei Punkte mit demselben
      // Symbol sind in einer Leiste, die man überfliegt, ein Punkt.
      icon: MessagesSquare,
      abzeichen: 'portalVorgaengeOffen',
    },
  },
  // **Die Gruppe «System» — hier stellt der Kunde sein Buchhaltungssystem ein**
  // (`PF-systemreiter`, 2026-10-06, Spec Systemeinstellungen `D-52`). Sie löst
  // die acht Verweise ab, die bis dahin einzeln und ohne gemeinsame Überschrift
  // ganz unten in dieser Leiste standen und im Quelltext «Betrieb-Verweise»
  // hiessen: «Benutzer», «Systemstatus», «Kontierungsregeln», «API-Tokens»,
  // «Anfragen», «Webhooks», «Portal-Vorgänge» und «Lizenz». «Betrieb» war nie
  // eine Gruppe der Oberfläche, sondern ein Wort der Entwicklung für «hat
  // keinen Platz im Tagesgeschäft» — ein Sammelbecken. Eine Menügruppe ist eine
  // Produktaussage, und diese lautet: Hier wird eingestellt. Seit dieser
  // Änderung gibt es in der Oberfläche keine Gruppe «Betrieb» mehr.
  //
  // **Eine Kategorie, weil es jetzt mehrere Zeilen sind.** Ein Benutzer ohne
  // JEDE ihrer Fähigkeiten sieht die Gruppe nicht (`sichtbareEintraege()`
  // wirft eine leer gewordene Kategorie weg); mit einer einzigen sieht er sie
  // mit dieser einen Zeile. Die Zahl der erschöpften Webhook-Zustellungen
  // erscheint, solange die Gruppe zugeklappt ist, als Summe an ihr
  // (`kategorieAbzeichen()`).
  //
  // **Die Reihenfolge ist die der Einrichtung und danach die der Häufigkeit:**
  //
  // 1. «Einrichtung» (seit dem Stapeln von `PF-assistent`, die erste Zeile) und
  //    «Einstellungen» — Letztere **noch KEINE Zeile**, und das
  //    ist Absicht: Beide Seiten sind noch nicht gebaut (`PF-seite` Stufe B,
  //    Spec Abschnitt 12, und `PF-assistent`, Spec 6.5, Stufe A, Zug PF-Z3).
  //    Ein Menüeintrag ohne Ziel wird nicht angelegt (Kopfkommentar dieser
  //    Datei) — er behauptete einen Funktionsumfang, den es nicht gibt. Sie
  //    kommen mit ihrem eigenen Chip als ERSTE und ZWEITE Zeile dieser Gruppe,
  //    vor «Benutzer». «Ersteinrichtung» ist nur sichtbar, solange die
  //    Einrichtung offen ist (`einrichtung.guard.ts`, `EINRICHTUNG_OFFEN`); das
  //    entscheidet die Schale über den Zustand der Einrichtung und nicht diese
  //    Tabelle über eine Fähigkeit — sie wird dort bei ihrem Chip eine
  //    zustandsabhängige Zeile, nicht eine Fähigkeitszeile.
  // 3. «Benutzer» — ein neu eingerichteter Administrator richtet zuerst weitere
  //    Konten ein, bevor ihn interessiert, ob ein Hintergrundlauf durchkam.
  // 4. «API-Tokens» — ein Token für ein fremdes System richtet ein
  //    Administrator seltener ein als ein Konto für einen Kollegen.
  // 5. «Webhooks» — dieselbe Art Angelegenheit wie das Token (ein Geheimnis,
  //    einmal eingerichtet), deshalb direkt dahinter.
  // 6. «Lizenz» — einmal eingerichtet, danach nur angesehen, wenn das
  //    Hinweisband etwas sagt. Verabredet ist sie als Abschnitt der
  //    Einstellungsseite (Ära PF, `D-48`); bis es die gibt, dieser Verweis.
  // 7. **«Datensicherung» — Platz für die Ära DS, noch KEINE Zeile.** Ihre Spec
  //    ist in Arbeit; der Menüpunkt kommt mit ihrem eigenen Chip HIER, zwischen
  //    «Lizenz» und «Systemstatus». Eine Zeile mit einer Route, die es noch
  //    nicht gibt, ist ein Menüpunkt ohne Ziel.
  // 8. «Systemstatus», 9. «Anfragen», 10. «Kontierungsregeln» — die drei
  //    Auskunftsseiten über den Betrieb, alle hinter `system:read`: ob ein
  //    Hintergrundlauf durchkam, was an die Schnittstelle gefragt wurde, welche
  //    Regeln es gibt. Wer hier hinschaut, sucht eine Ursache, und das
  //    geschieht seltener, als dass er etwas einstellt.
  //
  // **Ein weiterer Eintrag ist eine Zeile in `punkte` und keine Änderung an
  // `Sidebar.tsx`** — beide Formen, Kategorie und Verweis, werden bereits
  // gerendert, und der Klappzustand liest seine Schlüssel aus dieser Tabelle.
  // Der Schlüssel `system` ist der einer KATEGORIE und kollidiert nicht mit der
  // Adresse `system` (der Systemstatus): Jener ist der Merkschlüssel des
  // Klappzustands, diese ein Adressname.
  {
    art: 'kategorie',
    key: 'system',
    label: 'System',
    // `Settings` und nicht `SlidersHorizontal`: Jenes hängt an «Guthaben-Korrekturen»
    // weiter oben, und die Gruppe soll in der Leiste ihr
    // eigenes Zeichen tragen.
    icon: Settings,
    punkte: [
      // «Einrichtung» (`PF-assistent`, 2026-09-30, Task 11; beim Stapeln am 2026-10-07 aus dem
      // achten Betrieb-Verweis in diese Gruppe gezogen, `D-52`): Solange die Ersteinrichtung
      // offen ist, führt die Schale jede Adresse hierher (`einstellungen/einrichtungslage.ts`);
      // danach ist der Punkt der Weg in den Änderungsmodus (Schritte 2, 3, 6, 7, `D-40`), bis
      // die Seite «Einstellungen» (Stufe B, `PF-seite`) ihn ablöst. Eigene Fähigkeit
      // `einstellung:manage`, die allein `admin` trägt (`D-19`). Kein Abzeichen, keine Haupthandlung.
      {
        route: { name: 'einrichtung' },
        label: 'Einrichtung',
        capability: 'einstellung:manage',
        icon: ClipboardCheck,
      },
      // «Übernahme» (`LX-webseite`, 2026-10-16, Task 33, Spec Lexware-Migration 20.2): die Seite
      // «Übernahme aus Lexware Office», der zweite Ort neben dem Einrichtungsschritt «Vorsystem».
      // Sichtbar nur mit `vorsystem:import` (allein `admin`, `D-74`) UND wenn die Übernahme angelegt
      // ist (`bedingung`): Eine Installation ohne Vorsystem sieht den Punkt nie. Direkt hinter
      // «Einrichtung», weil sie deren Fortsetzung nach dem Abschluss ist. Kein Abzeichen: Ein
      // laufender Auftrag ist keine Zahl, die man auf null bringt.
      {
        route: { name: 'vorsystem' },
        label: 'Übernahme',
        capability: 'vorsystem:import',
        bedingung: 'vorsystemAngelegt',
        // `ArrowRightLeft`: Daten wandern von einem System ins andere; jedes andere Zeichen der
        // Leiste ist schon vergeben.
        icon: ArrowRightLeft,
      },
      // «Belegvorlage» (`BV-nullseite`, 2026-10-15, Spec Belegvorlagen 11.1): die
      // Gestaltung der Belege — Fusszeile, Einleitung, Schluss und die Schalter für
      // Bankverbindung und Zahlungs-QR, mit dem PDF des Musterbelegs daneben. Sie
      // steht hier und nicht unter «Verkauf» neben den E-Mail-Vorlagen: Sie ändert
      // keinen einzelnen Beleg, sondern wie JEDER Beleg aussieht, und liegt damit in
      // der Gruppe der Einrichtung. Sichtbar mit `document:manage`, der Fähigkeit,
      // die die Leseendpunkte und die Vorschau wirklich verlangen; das Speichern
      // hängt auf der Seite an `belegvorlage:manage`, das Scharfschalten an
      // `belegvorlage:scharfschalten`, und darüber entscheidet bei jeder Anfrage der
      // Wächter neu. Kein Abzeichen und keine Haupthandlung: Es gibt keine Zahl, die
      // auf null gehen soll, und keine Anlegeadresse — eine Fassung entsteht im Formular.
      {
        route: { name: 'belegvorlagen' },
        label: 'Belegvorlage',
        capability: 'document:manage',
        icon: LayoutTemplate,
      },
      // Keine Abzeichen bei «Benutzer», «Systemstatus», «Anfragen»: Bei
      // «Benutzer» wäre die Zahl «Konten mit ausstehendem Passwortwechsel»
      // denkbar, aber `AbzeichenQuelle` kennt sie nicht (Vorschlag im
      // Abschlussbericht statt einer Erweiterung im Vorbeigehen); bei
      // «Systemstatus» wäre sie «Anzahl roter Bereiche» und entstünde erst aus
      // dem Abruf der Seite selbst — ein Abzeichen, das die Seite laden muss,
      // um die Zahl zu kennen, ist kein Abzeichen.
      //
      // Keine Haupthandlung bei «Benutzer», «API-Tokens», «Webhooks»:
      // «Anlegen» sitzt in einem Dialog auf der Seite selbst (dieselbe Bauart
      // wie «Konto anlegen» bei `bank-accounts`), und bei Token und Webhook
      // erscheint das Geheimnis dort genau einmal — eine eigene Anlegeadresse
      // trüge es in den Verlauf des Browsers.
      {
        route: { name: 'users' },
        label: 'Benutzer',
        capability: 'user:read',
        icon: UserCog,
      },
      // **Eigene Fähigkeit, nicht `user:read`.** `apitoken:manage` trägt allein
      // die Rolle `admin` (`apps/api/src/auth/capabilities.ts`), und alle vier
      // Endpunkte der Seite verlangen genau sie — es gibt keine Lesefähigkeit
      // daneben, die einen halbsichtbaren Punkt rechtfertigte.
      {
        route: { name: 'api-tokens' },
        label: 'API-Tokens',
        capability: 'apitoken:manage',
        icon: KeyRound,
      },
      // **Eigene Fähigkeit, nicht `system:read`.** `webhooks:manage` trägt
      // allein die Rolle `admin`, und alle fünf Endpunkte der Seite verlangen
      // genau sie. Die eine Ausnahme steht auf der Seite selbst: Der Starttag
      // der Aufladungsautomatik wird unter `system:read` GELESEN und unter
      // `webhooks:manage` GESETZT — wer den Menüpunkt sieht, trägt ohnehin
      // beide.
      //
      // **Abzeichen seit `WH-navabzeichen` (2026-09-10):** Die Zahl der
      // erschöpften Zustellungen über alle aktiven Endpunkte — ein Hinweis
      // darauf, dass ein Empfänger Nachsenden braucht. Sichtbar nur für
      // `webhooks:manage`, dieselbe Fähigkeit wie der Punkt selbst.
      {
        route: { name: 'webhooks' },
        label: 'Webhooks',
        capability: 'webhooks:manage',
        // `Webhook` und nicht `Share2`: Jenes hängt schon am DATEV-Export
        // weiter oben, und zwei Punkte mit demselben Symbol sind in einer
        // Leiste, die man überfliegt, ein Punkt.
        icon: Webhook,
        abzeichen: 'webhooksErschoepft',
      },
      // «E-Mail-Eingang» (`EM-web`, 2026-11-04, Spec E-Mail-Eingang 6): die
      // Postfächer, aus denen der Abruf Eingangsrechnungen holt, ihre Regeln
      // und das Protokoll. Direkt hinter «Webhooks», weil beide eine Verbindung
      // zu einem fremden System einrichten, die man einmal anlegt und danach
      // nur ansieht, wenn etwas nicht ankam. `maileingang:manage` trägt allein
      // `admin` (`D-14`); das Protokoll liest die Schnittstelle zusätzlich unter
      // `incoming:read`, der Menüpunkt hängt an der Verwaltung. Abzeichen: die
      // Zahl der aktiven Konten mit Fehler.
      {
        route: { name: 'maileingang' },
        label: 'E-Mail-Eingang',
        capability: 'maileingang:manage',
        // `MailCheck` und nicht `Mail`: Jenes hängt an «E-Mail-Vorlagen».
        icon: MailCheck,
        abzeichen: 'maileingangFehler',
      },
      // «Workflows» (`WF-web`, Spec 2026-11-04-workflows-design.md Abschnitt 6) direkt
      // hinter «Webhooks»: dieselbe Art Einstellung — was dieses Haus von sich aus tut,
      // wenn etwas geschieht. `workflow:read`, die Fähigkeit der Liste; Anlegen,
      // Speichern und Scharfschalten hängen auf der Seite an `workflow:manage`. Kein
      // Abzeichen: Fehlgeschlagene Läufe zeigt die Liste je Zeile, und eine Zahl dafür
      // gäbe es nur aus dem Abruf der Seite selbst.
      {
        route: { name: 'workflows' },
        label: 'Workflows',
        capability: 'workflow:read',
        icon: Workflow,
      },
      // **Eigene Fähigkeit `lizenz:verwalten`** (`LZ-erneuerung`, 2026-09-30,
      // Spec Lizenzierung 8), allein beim Administrator; alle vier Routen der
      // Seite verlangen genau sie. Kein Abzeichen: Die Lage zeigt das
      // Hinweisband in der Schale jedem Angemeldeten; ein zweites Signal hier
      // wäre dieselbe Aussage doppelt.
      {
        route: { name: 'lizenz' },
        label: 'Lizenz',
        capability: 'lizenz:verwalten',
        // `ShieldCheck` und nicht `KeyRound`: Jenes hängt an «API-Tokens», und
        // zwei Punkte mit demselben Symbol sind in einer Leiste, die man
        // überfliegt, ein Punkt.
        icon: ShieldCheck,
      },
      // «Datensicherung» (`DS-web`, 2026-10-07, Spec Datensicherung 10, `D-25`),
      // beim Stapeln in die Gruppe «System» gezogen (Zug 224): zwischen «Lizenz»
      // und «Systemstatus», wie der Kopfkommentar der Gruppe es vorsieht.
      // `sicherung:read`, die Fähigkeit der zwei Leseendpunkte (Status, Läufe);
      // die Schaltflächen und die Einrichtung hängen auf der Seite an
      // `sicherung:manage`, und darüber entscheidet bei jeder Anfrage der Wächter
      // neu. Kein Abzeichen: «Fehlläufe seit dem letzten Erfolg» entstünde erst
      // aus dem Abruf der Seite selbst — dieselbe Erwägung wie bei «Systemstatus».
      {
        route: { name: 'sicherung' },
        label: 'Datensicherung',
        capability: 'sicherung:read',
        // `DatabaseBackup` und kein Schild: `ShieldCheck` hängt an «Lizenz» und
        // `Activity` an «Systemstatus».
        icon: DatabaseBackup,
      },
      {
        route: { name: 'system' },
        label: 'Systemstatus',
        capability: 'system:read',
        icon: Activity,
      },
      // «Anfragen» (`W-debugger-ui`, 2026-09-01): der Anfrage-Debugger über das
      // Zugriffsprotokoll — jede Anfrage an die Schnittstelle, erfolgreich oder
      // nicht, und bei einem Fehlschlag, was übergeben und was zurückgesendet
      // wurde. Dieselbe Fähigkeit `system:read` wie der Systemstatus, aus
      // demselben Grund: Er nennt Pfade, Statuscodes, Anmeldeadressen und
      // Tokenbeschreibungen und keine einzelne Buchung, die je Fachrolle
      // sichtbar sein müsste.
      {
        route: { name: 'anfragen' },
        label: 'Anfragen',
        capability: 'system:read',
        icon: Radar,
      },
      // «Kontierungsregeln» (`W-regeluebersicht`, 2026-08-31, Stufe 1 des
      // Regel-UI-Wunschs): das Regelregister, NUR LESEND — eine Auskunft über
      // den Betrieb der Regel-Engine, dieselbe Fähigkeit `system:read` aus
      // demselben Grund wie der Systemstatus: Es nennt Kennungen,
      // Aktivierungsgrenzen und Zählungen und keine einzelne Buchung (dafür
      // bleibt `GET /api/v1/kontierungen/automatisch` hinter `bank:read`).
      // **Bleibt hier und wandert nicht zu «Buchhaltung»:** Es zeigt, welche
      // Regeln es gibt, nicht was sie gebucht haben, und eine Regel ist Code
      // (`REI-2 = B`), keine Einstellung des Kunden — deshalb ganz unten in der
      // Gruppe, als Auskunft und nicht als Stellschraube.
      //
      // Kein Abzeichen: Es gibt keine wartende Zahl — eine Regel läuft oder sie
      // läuft nicht, das ist kein Rückstand, den man auf null bringt.
      {
        route: { name: 'regeln' },
        label: 'Kontierungsregeln',
        capability: 'system:read',
        icon: Scale,
      },
    ],
  },
];

/**
 * Die Unterseiten und der Punkt, zu dem sie gehören.
 *
 * Die Detailansicht einer Buchung gehört zum Journal, die Kundenmaske zu den
 * Kunden — sonst erlischt die Markierung, sobald jemand eine Maske öffnet, und
 * die Oberfläche sagt «du bist nirgends».
 */
export const SECTION: Partial<Readonly<Record<Route['name'], Route['name']>>> = {
  entry: 'journal',
  customer: 'customers',
  workflow: 'workflows',
  'workflow-laeufe': 'workflows',
  'customer-new': 'customers',
  vendor: 'vendors',
  'vendor-new': 'vendors',
  document: 'documents',
  'document-new': 'documents',
  // Vorlagenmaske und Anlagemaske gehören zum Punkt «Aborechnungen».
  aborechnung: 'aborechnungen',
  'aborechnung-new': 'aborechnungen',
  // Die drei Unterseiten eines Bankkontos gehören zum Punkt «Bankkonten» —
  // Kontoansicht, Einlesebereich und Auszugsliste.
  'bank-account': 'bank-accounts',
  'bank-import': 'bank-accounts',
  'bank-statements': 'bank-accounts',
  // Die zwei Unterseiten eines Zahldienstkontos gehören zu demselben Punkt. Ein
  // eigener Menüpunkt «Zahldienste» behauptete einen Unterschied, den das
  // Datenmodell nicht macht — sie SIND Geldkonten.
  'zahldienst-abruf': 'bank-accounts',
  'zahldienst-klaerliste': 'bank-accounts',
  // Die Erfassungsmaske gehört zum Eingangskorb.
  'eingang-erfassen': 'eingang',
  // Der Detailsatz gehört zur Liste der Guthabenabstimmung.
  'guthaben-abstimmung-detail': 'guthaben-abstimmung',
  // **Die Bereichsseite der Auswertungen gehört zur Kategorie, und der
  // einzige Weg dorthin führt über einen ihrer Punkte.** `findeEintrag()`
  // erreicht eine Kategorie ausschliesslich über einen ihrer Unterpunkte;
  // ohne diese Zeile blieben Markierung UND Brotkrumen auf `/auswertungen`
  // leer, und die Oberfläche sagte «du bist nirgends» — genau das, wogegen
  // der Kopfkommentar dieser Tabelle geschrieben ist.
  //
  // **Der erste Punkt und nicht ein neunter, unsichtbarer.** Die Alternative
  // wäre ein Menüpunkt «Überblick» über den acht (seit `BWA-grund`,
  // 2026-09-14) — ein Klick, der auf eine Seite führt, die keine Kennzahl
  // zeigt. Die acht Unterseiten stehen selbst im Menü; die Bereichsseite ist
  // der Ort, an dem man landet, wenn man die Adresse tippt, und sie erbt
  // deshalb die Markierung des ersten.
  auswertungen: 'auswertung-umsatz',
};

/** Der Punkt, zu dem eine Adresse gehört. */
export function sectionOf(route: Route): Route['name'] {
  return SECTION[route.name] ?? route.name;
}

/** Kategorie und Punkt einer Adresse — die Quelle für Markierung und Brotkrumen. */
export interface NavFundstelle {
  readonly kategorie?: NavKategorie;
  readonly punkt?: NavPunkt;
}

/**
 * Kategorie und Punkt zu einer Adresse.
 *
 * **Beide Felder dürfen fehlen, und das ist der Regelfall für `/profil`.**
 * Die Profilseite steht in keiner Kategorie und soll auch keine markieren —
 * sie wird über die Sitzungsecke der Menüspalte angesprungen, nicht über
 * einen Menüpunkt.
 * Eine Zuordnung «gehört irgendwie zu den Stammdaten» wäre erfunden — und die
 * Brotkrumen zeigten sie dann an.
 */
export function findeEintrag(route: Route): NavFundstelle {
  const section = sectionOf(route);
  for (const eintrag of NAV) {
    if (eintrag.art === 'verweis') {
      if (eintrag.punkt.route.name === section) return { punkt: eintrag.punkt };
      continue;
    }
    const punkt = eintrag.punkte.find((kandidat) => kandidat.route.name === section);
    if (punkt !== undefined) return { kategorie: eintrag, punkt };
  }
  return {};
}

/**
 * Die summierte Zahl einer Kategorie — oder `undefined`.
 *
 * **Angezeigt wird sie nur im ZUGEKLAPPTEN Zustand.** Offen sitzen die Zahlen
 * an den Unterpunkten, und eine Summe daneben zählte dasselbe zweimal.
 *
 * `undefined` und nicht `0`: Eine Kategorie, deren Kinder keine Zählquelle
 * haben, trägt kein Abzeichen — und eine, deren Zähler alle bei null stehen,
 * ebenso wenig. Eine Null ist keine Nachricht.
 */
export function kategorieAbzeichen(
  kategorie: NavKategorie,
  stand: Abzeichenstand,
): number | undefined {
  let summe = 0;
  for (const punkt of kategorie.punkte) {
    if (punkt.abzeichen === undefined) continue;
    summe += stand[punkt.abzeichen] ?? 0;
  }
  return summe > 0 ? summe : undefined;
}

/**
 * Die Einträge, die dieser Anwender sehen darf.
 *
 * **Erst die Punkte filtern, dann die leer gewordenen Kategorien wegwerfen** —
 * in dieser Reihenfolge, denn ob eine Kategorie leer ist, steht erst nach dem
 * Filtern fest. Eine leere Kategorie «Buchhaltung» wäre eine Behauptung über
 * Rechte, die der Anwender nicht hat: Sie benennt einen Bereich und führt
 * nirgendwohin.
 *
 * **Ausblenden ist Bequemlichkeit und ausdrücklich kein Schutz**; darüber
 * entscheidet der Wächter der API bei jeder Anfrage neu.
 */
export function sichtbareEintraege(
  capabilities: ReadonlySet<string>,
  bedingungen: ReadonlySet<NavBedingung> = new Set<NavBedingung>(),
  abgeschaltet: ReadonlySet<NavFunktion> = new Set<NavFunktion>(),
): readonly NavEintrag[] {
  const sichtbar: NavEintrag[] = [];
  const darfSehen = (punkt: NavPunkt): boolean =>
    (punkt.capability === undefined || capabilities.has(punkt.capability)) &&
    (punkt.capabilityAny === undefined || punkt.capabilityAny.some((c) => capabilities.has(c))) &&
    (punkt.bedingung === undefined || bedingungen.has(punkt.bedingung)) &&
    (punkt.funktion === undefined || !abgeschaltet.has(punkt.funktion));
  for (const eintrag of NAV) {
    if (eintrag.art === 'verweis') {
      if (darfSehen(eintrag.punkt)) sichtbar.push(eintrag);
      continue;
    }
    const punkte = eintrag.punkte.filter(darfSehen);
    if (punkte.length > 0) sichtbar.push({ ...eintrag, punkte });
  }
  return sichtbar;
}

/**
 * Die Schlüssel aller Kategorien.
 *
 * Sie sind der Anfangswert des Klappzustands — **alle offen** — und zugleich
 * die Sollliste, an der ein gemerkter Zustand gemessen wird. Eine Kategorie,
 * die es nicht mehr gibt, verschwindet damit von selbst aus dem Speicher.
 */
export function alleKategorieSchluessel(): readonly string[] {
  return NAV.filter((eintrag): eintrag is NavKategorie => eintrag.art === 'kategorie').map(
    (eintrag) => eintrag.key,
  );
}

/**
 * Die Brotkrumen einer Adresse — zwei bis drei Segmente, immer Text.
 *
 * **Unterseiten erben das Segment ihres Punktes und hängen nichts an.**
 * `/belege/<id>` zeigt «FameAccounting / Verkauf / Belege» und nicht die
 * Belegnummer: Die steht als Überschrift auf der Seite, und ein Brotkrumenpfad,
 * der bei jedem Datensatz anders lautet, ist keine Orientierung mehr, sondern
 * eine zweite Überschrift.
 */
export function brotkrumen(route: Route): readonly string[] {
  const { kategorie, punkt } = findeEintrag(route);
  const segmente = ['FameAccounting'];
  if (kategorie !== undefined) segmente.push(kategorie.label);
  if (punkt !== undefined) segmente.push(punkt.label);
  else if (route.name === 'profile') segmente.push('Mein Konto');
  return segmente;
}

/**
 * Die Haupthandlung der aktuellen Adresse — oder `undefined`.
 *
 * **Auf der Anlegemaske selbst gibt es sie nicht.** Ein «Neu», das auf die
 * Seite führt, auf der man schon steht, ist kein Weg, sondern ein Knopf, der
 * nichts tut.
 *
 * **Verglichen wird die ADRESSE (`toPath`), nicht nur der Adressname.** Bis
 * Chip 5 (`V-belegbank`) fielen beide zusammen: Jedes Ziel war eine eigene
 * Route (`document-new`, `customer-new`, `vendor-new`), und `aktion.ziel.name
 * === route.name` traf genau dann zu, wenn man schon auf der Zielseite stand.
 * `bank-accounts` und `eingang` haben **keine eigene Anlegeadresse** — ihr Ziel
 * ist dieselbe Route mit `neu: true` (Spec 7.3: Dialog der Liste,
 * beziehungsweise die Ablagefläche derselben Seite). Ein Vergleich nach `.name`
 * allein verglich dort `'bank-accounts' === 'bank-accounts'` und unterdrückte
 * den Knopf GENAU auf der Seite, auf der er erscheinen soll. `toPath` sieht
 * das `?neu=1` mit und unterscheidet die beiden Fälle richtig — und
 * unterdrückt den Knopf trotzdem, sobald man (durch einen Klick auf ihn) auf
 * `?neu=1` selbst steht.
 */
export function hauptaktionFuer(
  route: Route,
  capabilities: ReadonlySet<string>,
): NavHauptaktion | undefined {
  const aktion = findeEintrag(route).punkt?.hauptaktion;
  if (aktion === undefined) return undefined;
  if (toPath(aktion.ziel) === toPath(route)) return undefined;
  return capabilities.has(aktion.capability) ? aktion : undefined;
}
